import React, { ReactChild, Suspense } from 'react'
import { BrowserRouter as Router, Route } from 'react-router-dom'
import { BabylonIntl, useFormatMessage, useIntl } from '@babylon/intl'
import { ThemeProvider, generateTheme, ModalProvider } from '@babylon/medkit'
import {
  ThemeProvider as WpThemeProvider,
  Box,
  dnaTheme,
} from '@babylon/web-platform-ui'
import '@babylon/fonts/Visuelt'
import '@babylon/fonts/Inter'

import { getTheme } from '@babylon/dna-web'
import {
  Header,
  Footer,
  USFooter,
  Main,
  ErrorBoundary,
  Alert,
  CookieBanner,
  getFeatureFlags,
} from '@/components'
import { URL_PREFIX } from '@/constants'

import {
  useBranding,
  useConfig,
  useContent,
  useLocalisedContent,
} from '@/redux/partnerConfig/selectors'
import { FooterLink } from '@/types/partnerConfig'
import { SignUp, ErrorPage } from '@/views'
import styles from '@/App.module.scss'
import messages from '@/messages'
import {
  useCookiePolicyLink,
  useCTAs,
  useRegulatoryPolicyLink,
} from './redux/productConfig/selectors'
import { getInitialLocale, getMessagesOnLocaleChange } from './i18n'
import { useUserLocale } from './redux/userConfig/selectors'
import { LogoutRoute } from './components/Logout'

const App = () => {
  const translate = useFormatMessage()
  const partnerBranding = useBranding()
  const { useDnaV2Design } = useConfig()
  const { locale } = useIntl()
  const { footerLinks } = useLocalisedContent(locale)
  const cookieLink: FooterLink = {
    key: 'cookies',
    link: useCookiePolicyLink(),
  }

  const footerItems: FooterLink[] = [...footerLinks, cookieLink]

  const regulatoryPolicyLink = useRegulatoryPolicyLink()

  if (regulatoryPolicyLink) {
    footerItems.push({
      key: 'regulatory',
      link: regulatoryPolicyLink,
    })
  }

  const { logo, theme } = partnerBranding
  const medkitTheme = theme ? generateTheme(theme) : {}

  // DNA Provider

  // Get feature flags set in cookies and alert the user they are applied
  getFeatureFlags()

  // DNA Provider - currently only enabled for the components used in the new
  // book CTA in SummaryPage.tsx
  const appointmentBookingEnabled = useCTAs()?.includes('book_appointment')

  const DnaProvider = React.lazy(
    () => import(/* webpackChunkName: "BookCTA" */ './DnaProvider')
  )

  const dnaProviderTheme = getTheme({
    isA11yMode: false,
    isDarkMode: false,
    themeName: 'Babylon',
  })

  type Locale = 'en-US' | 'es-US' | 'en-GB' | 'en-CA' | 'fr-CA'

  const userLocale = useUserLocale()
  const content = useContent()
  const { acceptLanguage: partnerDefaultLanguage } = useConfig()
  const availableLocales = Object.keys(content)
  const initialLocale = getInitialLocale(
    availableLocales,
    partnerDefaultLanguage,
    userLocale
  )

  const LazyDnaProvider = ({ children }: { children: ReactChild }) =>
    appointmentBookingEnabled ? (
      <Suspense fallback="<div>Loading</div>">
        <DnaProvider locale={locale as Locale} theme={dnaProviderTheme}>
          <BabylonIntl
            initialLocale={initialLocale}
            getMessagesOnLocaleChange={(locale: string) =>
              getMessagesOnLocaleChange({ locale })
            }
          >
            {children}
          </BabylonIntl>
        </DnaProvider>
      </Suspense>
    ) : (
      <BabylonIntl
        initialLocale={initialLocale}
        getMessagesOnLocaleChange={(locale: string) =>
          getMessagesOnLocaleChange({ locale })
        }
      >
        {children}
      </BabylonIntl>
    )

  if (useDnaV2Design) {
    return (
      <div className={styles.App}>
        <LazyDnaProvider>
          <ThemeProvider theme={medkitTheme} root>
            <WpThemeProvider theme={dnaTheme}>
              <Box bg="light.background.backgroundSecondary">
                <Router>
                  <Route path={`/${URL_PREFIX}/:partnerSlug/logout`}>
                    <LogoutRoute />
                  </Route>
                  <ErrorBoundary Component={ErrorPage}>
                    <ModalProvider
                      closeButtonText={translate(messages.closeButtonText)}
                      backButtonText={translate(messages.backButtonText)}
                    >
                      <Alert />
                      <CookieBanner />
                      <Header logos={logo} />
                      <Main>
                        <Route path={`/${URL_PREFIX}/:partnerSlug`}>
                          <SignUp />
                        </Route>
                      </Main>
                      <USFooter />
                    </ModalProvider>
                  </ErrorBoundary>
                </Router>
              </Box>
            </WpThemeProvider>
          </ThemeProvider>
        </LazyDnaProvider>
      </div>
    )
  }

  return (
    <div className={styles.App}>
      <LazyDnaProvider>
        <ThemeProvider theme={medkitTheme} root>
          <Router>
            <ErrorBoundary Component={ErrorPage}>
              <ModalProvider
                closeButtonText={translate(messages.closeButtonText)}
                backButtonText={translate(messages.backButtonText)}
              >
                <Alert />
                <CookieBanner />
                <Header logos={logo} />
                <Main>
                  <Route path={`/${URL_PREFIX}/:partnerSlug`}>
                    <SignUp />
                  </Route>
                </Main>
                <Footer links={footerItems} />
              </ModalProvider>
            </ErrorBoundary>
          </Router>
        </ThemeProvider>
      </LazyDnaProvider>
    </div>
  )
}

export default App
